import type { TextAreaProps, TextAreaRef } from 'antd/es/input/TextArea';
import { RefObject, useEffect, useLayoutEffect, useState } from 'react';

// Wraps like a typical subject or description, so an empty field can still be dragged open to about the room its limit needs.
const SAMPLE_TEXT =
  'Reviewed the draft agreement with the client and discussed the revised payment terms. ';

const WRAP_AFFECTING_STYLES = [
  'width',
  'font-family',
  'font-size',
  'font-weight',
  'letter-spacing',
  'line-height',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'border-top-width',
  'border-right-width',
  'border-bottom-width',
  'border-left-width',
  'word-break',
  'overflow-wrap',
  'white-space',
] as const;

const buildSampleText = (length: number) =>
  SAMPLE_TEXT.repeat(Math.ceil(length / SAMPLE_TEXT.length)).slice(0, length);

/** Border-box height the textarea needs to show `text` at its current width, even with its scrollbar showing. */
export const measureTextHeight = (textarea: HTMLTextAreaElement, text: string) => {
  const computed = getComputedStyle(textarea);
  const probe = document.createElement('textarea');
  for (const property of WRAP_AFFECTING_STYLES) {
    probe.style.setProperty(property, computed.getPropertyValue(property));
  }
  probe.style.setProperty('box-sizing', 'border-box');
  probe.style.setProperty('position', 'absolute');
  probe.style.setProperty('visibility', 'hidden');
  probe.style.setProperty('left', '-9999px');
  probe.style.setProperty('height', '0');
  probe.style.setProperty('min-height', '0');
  probe.style.setProperty('overflow-x', 'hidden');
  // A scrollbar that is already showing stays while it is still needed, so wrap as narrow as the field does with it.
  probe.style.setProperty('overflow-y', 'scroll');
  probe.value = text;

  document.body.appendChild(probe);
  const verticalBorder =
    (Number.parseFloat(computed.getPropertyValue('border-top-width')) || 0) +
    (Number.parseFloat(computed.getPropertyValue('border-bottom-width')) || 0);
  const height = probe.scrollHeight + verticalBorder;
  probe.remove();
  return height;
};

/** Runs `callback` once the browser has painted the next frame, and returns a function that cancels it. */
const runAfterNextPaint = (callback: () => void) => {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  // A frame callback still runs before that frame paints; the task it queues runs after.
  const frame = requestAnimationFrame(() => {
    timeout = setTimeout(callback);
  });
  return () => {
    cancelAnimationFrame(frame);
    clearTimeout(timeout);
  };
};

/**
 * The tallest a drag-resizable textarea should go: enough for its current text or for a typical
 * text at its character limit, whichever is taller, re-measured as the width or text changes.
 * Call `onTextChange` from the field's React `onChange` so typing into an uncontrolled field is measured too.
 */
export const useFitContentMaxHeight = (
  textareaRef: RefObject<TextAreaRef | null>,
  maxLength: number,
  enabled: boolean,
  value: TextAreaProps['value'],
) => {
  const [width, setWidth] = useState(0);
  const [fontLoadCount, setFontLoadCount] = useState(0);
  const [textChangeCount, setTextChangeCount] = useState(0);
  const [limitHeight, setLimitHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const getTextarea = () => textareaRef.current?.resizableTextArea?.textArea;
  const measureContentHeight = (textarea: HTMLTextAreaElement) =>
    setContentHeight(measureTextHeight(textarea, textarea.value));

  useLayoutEffect(() => {
    const textarea = getTextarea();
    if (!enabled || !textarea) return;

    setWidth(textarea.offsetWidth);
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(() => setWidth(textarea.offsetWidth));
    resizeObserver?.observe(textarea);
    // Poppins loads with font-display: swap, so a measurement taken on the fallback font goes stale.
    const onFontsLoaded = () => setFontLoadCount((count) => count + 1);
    document.fonts?.addEventListener('loadingdone', onFontsLoaded);

    return () => {
      resizeObserver?.disconnect();
      document.fonts?.removeEventListener('loadingdone', onFontsLoaded);
    };
  }, [enabled]);

  useLayoutEffect(() => {
    const textarea = getTextarea();
    if (!enabled || !textarea || !width) return;
    setLimitHeight(measureTextHeight(textarea, buildSampleText(maxLength)));
  }, [enabled, width, maxLength, fontLoadCount]);

  useLayoutEffect(() => {
    const textarea = getTextarea();
    if (!enabled || !textarea || !width) return;
    measureContentHeight(textarea);
  }, [enabled, width, fontLoadCount]);

  // The text changes on every keystroke, so this probe waits until it has painted; rare width and font changes stay synchronous above.
  useEffect(() => {
    const textarea = getTextarea();
    if (!enabled || !textarea || !width) return;
    return runAfterNextPaint(() => measureContentHeight(textarea));
  }, [value, textChangeCount]);

  const measuredMaxHeight = Math.max(limitHeight, contentHeight);

  return {
    maxHeight: enabled && measuredMaxHeight > 0 ? measuredMaxHeight : undefined,
    // Not a native input listener: its re-render lands before React's onChange and reverts each keystroke.
    onTextChange: () => setTextChangeCount((count) => count + 1),
  };
};
